import { useQContent } from '@exam-management/common/qcontent-provider';
import { type QuestionPStats } from '@exam-management/graphql';
import {
  type QContentDetailFormContextData,
  getDifficultyCriteria,
  DEFAULT_CRITERIA,
} from '@exam-management/qcontent-management';
import {
  Dialog,
  DialogTitle,
  Divider,
  type DialogProps,
  DialogActions,
  Button,
} from '@shared/components';
import { formatDate, zodResolver } from '@shared/utils';
import { useMemo, useEffect } from 'react';
import { useForm, useFormContext, FormProvider } from 'react-hook-form';
import { z } from 'zod';

import { getPStatId } from '../../utils';
import {
  UpdatePUseList,
  type UpdatePUseListItem,
  type DifficultyLevel,
} from '../update-p-use-list';
interface UpdatePUseModalProps extends DialogProps {
  onClose: () => void;
  open: boolean;
}

const UpdatePUseModalSchema = z.object({
  pUseId: z.string(),
});
type UpdatePUseModalFormInput = z.infer<typeof UpdatePUseModalSchema>;

export default function UpdatePUseModal(props: Readonly<UpdatePUseModalProps>) {
  const { onClose, open: isOpen } = props;

  const { qContent } = useQContent();
  const qContentFormContext = useFormContext<QContentDetailFormContextData>();

  const rows: UpdatePUseListItem[] =
    qContent.metadata.pStats?.map((pStat) => {
      return {
        id: getPStatId(pStat),
        pStatPreview: pStat.pValue.toFixed(2),
        pStat: pStat.pValue,
        selected:
          qContentFormContext
            .getValues()
            .information.pStats?.find(
              (selectedPStat) => getPStatId(selectedPStat) === getPStatId(pStat),
            )?.selected ?? null,
        difficulty: getDifficultyCriteria(DEFAULT_CRITERIA, pStat.pValue)
          ?.difficultyLevel as DifficultyLevel,
        numberOfExaminee: pStat.sampleSize,
        calculatedDate: formatDate(pStat.calculatedAt),
        startedDate: formatDate(pStat.firstUsedAt),
        updatedBy: pStat.updatedUserFullName,
      };
    }) ?? [];

  const defaultPUseId = useMemo(() => {
    const selectedPStat = qContentFormContext
      .getValues()
      .information.pStats?.find((pStat) => pStat?.selected === true);

    if (!selectedPStat) return '';

    return rows.find((row) => row.id === getPStatId(selectedPStat))?.id ?? '';
  }, [qContentFormContext, rows]);

  const formContext = useForm<UpdatePUseModalFormInput>({
    defaultValues: { pUseId: defaultPUseId },
    resolver: zodResolver(UpdatePUseModalSchema),
  });

  useEffect(() => {
    formContext.reset({ pUseId: defaultPUseId });
  }, [defaultPUseId]);

  const handleClose = () => {
    formContext.reset();
    onClose();
  };

  const handleSelectRow = (selectedPUseId: string | undefined) => {
    if (selectedPUseId) {
      formContext.setValue('pUseId', selectedPUseId, { shouldDirty: true });
    } else {
      formContext.setValue('pUseId', '', { shouldDirty: true });
    }
  };

  const handleSubmit = async (data: UpdatePUseModalFormInput) => {
    const selectedPStat = qContent.metadata.pStats?.find(
      (pStat) => getPStatId(pStat) === data.pUseId,
    );
    const currentInformation = qContentFormContext.getValues().information;
    if (selectedPStat) {
      const currentPStats =
        (currentInformation.pStats?.map((pStat) => {
          if (getPStatId(pStat) === data.pUseId) {
            return { ...pStat, selected: true };
          }
          return { ...pStat, selected: null };
        }) as QuestionPStats[] | undefined) ?? [];
      const updatedQContentFormValues: QContentDetailFormContextData = {
        ...qContentFormContext.getValues(),
        information: {
          ...currentInformation,
          pStats: currentPStats,
          pValue: {
            ...currentInformation.pValue,
            pStat: selectedPStat.pValue,
            selected: 'pStat',
          },
        },
      };
      qContentFormContext.setValue('information', updatedQContentFormValues.information, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else {
      // all current pStats
      const currentPStats =
        (currentInformation.pStats?.map((pStat) => {
          return { ...pStat, selected: null };
        }) as QuestionPStats[] | undefined) ?? [];
      const updatedQContentFormValues: QContentDetailFormContextData = {
        ...qContentFormContext.getValues(),
        information: {
          ...currentInformation,
          pStats: currentPStats,
          pValue: {
            ...currentInformation.pValue,
            pStat: undefined,
            selected: 'pStat',
          },
        },
      };
      qContentFormContext.setValue('information', updatedQContentFormValues.information, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }

    handleClose();
  };

  return isOpen ? (
    <Dialog open={isOpen} maxWidth='lg' onClose={handleClose}>
      <FormProvider {...formContext}>
        <DialogTitle onClose={onClose}>ค่า P จากข้อมูลสถิติ</DialogTitle>
        <Divider />
        <UpdatePUseList rows={rows} onSelectRow={handleSelectRow} />
        <Divider />
        <DialogActions>
          <Button onClick={onClose} color='secondary' variant='outlined' sx={{ width: '100px' }}>
            Cancel
          </Button>
          <Button
            disabled={!formContext.formState.isDirty}
            onClick={formContext.handleSubmit(handleSubmit)}
            sx={{ width: '100px' }}
          >
            Apply
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  ) : null;
}
